import type React from 'react';
import { useState, useEffect } from 'react';
import {
  PageHeader, PageTitle, Card, Tab, TabBar,
  PrimaryButton, SecondaryButton,
  FormGroup, Input, Select, AlertBanner,
} from '../styled-components';
import { useFetchData } from '../hooks';
import { showToast } from '../hooks';
import { fetchOrThrow, getCSRFToken, CSRF_HEADER_NAME } from '../../common/utils';

// ---------------------------------------------------------------------------
// Profile tab
// ---------------------------------------------------------------------------

interface ProfileData {
  user_id: string;
  email: string;
  display_name: string | null;
}

function ProfileTab() {
  const { data, loading } = useFetchData<ProfileData | null>('/dashboard/api/settings/profile', null);
  const [displayName, setDisplayName] = useState('');
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (data) {
      setDisplayName(data.display_name ?? '');
      setDirty(false);
    }
  }, [data]);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      await fetchOrThrow('/dashboard/api/settings/profile', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', [CSRF_HEADER_NAME]: await getCSRFToken() },
        body: JSON.stringify({ display_name: displayName }),
      });
      showToast('Profile saved', 'success');
      setDirty(false);
    } catch { showToast('Failed to save profile', 'error'); }
    finally { setSaving(false); }
  }

  if (loading) return <div style={{ color: 'var(--text-secondary)', padding: 16 }}>Loading…</div>;

  return (
    <Card style={{ maxWidth: 520 }}>
      <form onSubmit={handleSave}>
        <FormGroup>
          <label>Email</label>
          <Input value={data?.email ?? ''} disabled style={{ opacity: 0.6, cursor: 'not-allowed' }} />
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
            Email cannot be changed. Contact support if needed.
          </div>
        </FormGroup>
        <FormGroup>
          <label>Display Name</label>
          <Input
            value={displayName}
            onChange={(e) => { setDisplayName(e.target.value); setDirty(true); }}
            placeholder="Your full name"
          />
        </FormGroup>
        <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
          <PrimaryButton type="submit" disabled={saving || !dirty}>
            {saving ? 'Saving…' : 'Save Changes'}
          </PrimaryButton>
          {dirty && (
            <SecondaryButton type="button" onClick={() => {
              setDisplayName(data?.display_name ?? '');
              setDirty(false);
            }}>
              Cancel
            </SecondaryButton>
          )}
        </div>
      </form>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Organization tab
// ---------------------------------------------------------------------------

interface OrgData {
  org_id: string;
  name: string;
  entity_type: string | null;
  region: string | null;
  use_case: string | null;
  compliance: string[];
}

const COMPLIANCE_OPTIONS = [
  { value: 'hipaa',   label: 'HIPAA' },
  { value: 'hitrust', label: 'HITRUST' },
  { value: 'soc2',    label: 'SOC 2' },
  { value: 'pci_dss', label: 'PCI DSS' },
  { value: 'gdpr',    label: 'GDPR' },
];

const ENTITY_TYPES = [
  { value: '', label: 'Select entity type…' },
  { value: 'startup', label: 'Startup' },
  { value: 'smb', label: 'Small / Mid-size Business' },
  { value: 'enterprise', label: 'Enterprise' },
  { value: 'agency', label: 'Agency / Consulting' },
  { value: 'nonprofit', label: 'Non-profit' },
  { value: 'individual', label: 'Individual / Hobbyist' },
];

const REGIONS = [
  { value: '', label: 'Select region…' },
  { value: 'us', label: 'United States' },
  { value: 'ca', label: 'Canada' },
  { value: 'eu', label: 'Europe' },
  { value: 'apac', label: 'Asia Pacific' },
  { value: 'latam', label: 'Latin America' },
  { value: 'other', label: 'Other' },
];

const USE_CASES = [
  { value: '', label: 'Select use case…' },
  { value: 'customer_support', label: 'Customer Support' },
  { value: 'sales', label: 'Sales / Lead Gen' },
  { value: 'scheduling', label: 'Appointment Scheduling' },
  { value: 'collections', label: 'Collections / Reminders' },
  { value: 'surveys', label: 'Surveys / Research' },
  { value: 'internal', label: 'Internal Tooling' },
  { value: 'other', label: 'Other' },
];

function OrgTab() {
  const { data, loading } = useFetchData<OrgData | null>('/dashboard/api/settings/org', null);
  const [form, setForm] = useState<Partial<OrgData>>({});
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (data) {
      setForm(data);
      setDirty(false);
    }
  }, [data]);

  function update(field: keyof OrgData, value: string | boolean | string[]) {
    setForm((f) => ({ ...f, [field]: value }));
    setDirty(true);
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      await fetchOrThrow('/dashboard/api/settings/org', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', [CSRF_HEADER_NAME]: await getCSRFToken() },
        body: JSON.stringify({
          name: form.name,
          entity_type: form.entity_type || null,
          region: form.region || null,
          use_case: form.use_case || null,
          compliance: form.compliance ?? [],
        }),
      });
      showToast('Organization settings saved', 'success');
      setDirty(false);
    } catch { showToast('Failed to save settings', 'error'); }
    finally { setSaving(false); }
  }

  if (loading) return <div style={{ color: 'var(--text-secondary)', padding: 16 }}>Loading…</div>;

  return (
    <Card style={{ maxWidth: 520 }}>
      <form onSubmit={handleSave}>
        <FormGroup>
          <label>Organization Name</label>
          <Input
            value={form.name ?? ''}
            onChange={(e) => update('name', e.target.value)}
            placeholder="Acme Corp"
            required
          />
        </FormGroup>
        <FormGroup>
          <label>Entity Type</label>
          <Select value={form.entity_type ?? ''} onChange={(e) => update('entity_type', e.target.value)}>
            {ENTITY_TYPES.map(({ value, label }) => <option key={value} value={value}>{label}</option>)}
          </Select>
        </FormGroup>
        <FormGroup>
          <label>Region</label>
          <Select value={form.region ?? ''} onChange={(e) => update('region', e.target.value)}>
            {REGIONS.map(({ value, label }) => <option key={value} value={value}>{label}</option>)}
          </Select>
        </FormGroup>
        <FormGroup>
          <label>Primary Use Case</label>
          <Select value={form.use_case ?? ''} onChange={(e) => update('use_case', e.target.value)}>
            {USE_CASES.map(({ value, label }) => <option key={value} value={value}>{label}</option>)}
          </Select>
        </FormGroup>

        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 12 }}>
            Compliance Needs
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 24px' }}>
            {COMPLIANCE_OPTIONS.map(({ value, label }) => {
              const checked = (form.compliance ?? []).includes(value);
              return (
                <label key={value} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => {
                      const current = form.compliance ?? [];
                      update('compliance', checked ? current.filter((v) => v !== value) : [...current, value]);
                    }}
                    style={{ width: 16, height: 16, accentColor: 'var(--accent-cyan)', cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: 14, color: 'var(--text-primary)' }}>{label}</span>
                </label>
              );
            })}
          </div>
        </div>

        <AlertBanner variant="cyan" style={{ marginBottom: 20 }}>
          Organization ID: <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12 }}>{data?.org_id ?? '—'}</span>
        </AlertBanner>

        <div style={{ display: 'flex', gap: 10 }}>
          <PrimaryButton type="submit" disabled={saving || !dirty}>
            {saving ? 'Saving…' : 'Save Changes'}
          </PrimaryButton>
          {dirty && (
            <SecondaryButton type="button" onClick={() => { if (data) { setForm(data); setDirty(false); } }}>
              Cancel
            </SecondaryButton>
          )}
        </div>
      </form>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Settings page
// ---------------------------------------------------------------------------

export function SettingsPage() {
  const [activeTab, setActiveTab] = useState<'profile' | 'org'>('profile');

  return (
    <div>
      <PageHeader>
        <PageTitle>Settings</PageTitle>
      </PageHeader>

      <TabBar>
        <Tab active={activeTab === 'profile'} onClick={() => setActiveTab('profile')}>Profile</Tab>
        <Tab active={activeTab === 'org'} onClick={() => setActiveTab('org')}>Organization</Tab>
      </TabBar>

      {activeTab === 'profile' && <ProfileTab />}
      {activeTab === 'org' && <OrgTab />}
    </div>
  );
}
